import { useState, useEffect } from "react";
import { useMusicPlayer } from "@/context/MusicPlayerContext";
import { useI18n } from "@/context/I18n";
import { fmtTime } from "@/lib/youtube";
import { isLiked, toggleLike } from "@/lib/likes";
import api, { coverUrl } from "@/lib/api";
import { toast } from "sonner";
import { Play, Pause, Plus, Trash2, Music2, ListEnd, CornerDownRight, ListPlus, Heart, Share2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function TrackRow({ track, index, tracks, onRemove, showAdd = true }) {
  const { playQueue, isCurrent, isPlaying, togglePlay, addNext, addToQueue } = useMusicPlayer();
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [liked, setLiked] = useState(isLiked(track.id));
  const active = isCurrent(track);

  useEffect(() => {
    const h = () => setLiked(isLiked(track.id));
    window.addEventListener("likes-changed", h);
    return () => window.removeEventListener("likes-changed", h);
  }, [track.id]);

  const like = (e) => { e.stopPropagation(); toggleLike(track.id); };

  const play = () => {
    if (active) togglePlay();
    else playQueue(tracks, index);
  };

  const openMenu = async () => {
    if (!menuOpen) {
      try {
        const { data } = await api.get("/music/playlists");
        setPlaylists(data.playlists);
      } catch (e) {}
    }
    setMenuOpen((o) => !o);
  };

  const addTo = async (pid) => {
    try {
      await api.post(`/music/playlists/${pid}/tracks`, { track_id: track.id });
      toast.success(t("added_to_playlist"));
      window.dispatchEvent(new Event("playlists-changed"));
    } catch (e) {
      toast.error(t("could_not_add"));
    }
    setMenuOpen(false);
  };

  const playNext = () => { addNext(track); toast.success(t("will_play_next")); setMenuOpen(false); };
  const queueEnd = () => { addToQueue(track); toast.success(t("added_to_queue")); setMenuOpen(false); };

  const share = async () => {
    const url = `${window.location.origin}/app?track=${track.id}`;
    const payload = { title: track.title, text: `${track.title} · ${track.artist} · SONLYO`, url };
    try {
      if (navigator.share) await navigator.share(payload);
      else { await navigator.clipboard.writeText(url); toast.success(t("copied")); }
    } catch (e) { /* user cancelled */ }
    setMenuOpen(false);
  };

  return (
    <div
      data-testid={`track-row-${track.id}`}
      className="group grid grid-cols-[24px_1fr_auto] items-center gap-3 px-3 py-2 rounded-md hover:bg-white/5 transition-colors"
    >
      <div className="flex items-center justify-center">
        <span className={`text-sm font-mono ${active ? "text-[#FF2A4D]" : "text-zinc-500"} group-hover:hidden`}>
          {active && isPlaying ? "♪" : index + 1}
        </span>
        <button data-testid={`play-track-${track.id}`} onClick={play} className="hidden group-hover:block text-white">
          {active && isPlaying ? <Pause className="w-4 h-4" fill="white" /> : <Play className="w-4 h-4" fill="white" />}
        </button>
      </div>

      <div className="flex items-center gap-3 min-w-0 cursor-pointer" onClick={play}>
        {track.thumbnail ? (
          <img src={coverUrl(track.thumbnail, 250)} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded bg-white/5 flex items-center justify-center shrink-0"><Music2 className="w-4 h-4 text-zinc-500" /></div>
        )}
        <div className="min-w-0">
          {track.album_id ? (
            <Link
              to={`/app/album/${track.album_id}`}
              onClick={(e) => e.stopPropagation()}
              title={t("go_to_album")}
              className={`text-sm font-medium truncate block w-fit max-w-full hover:underline ${active ? "text-[#FF2A4D]" : "text-white"}`}
            >
              {track.title}
            </Link>
          ) : (
            <p className={`text-sm font-medium truncate ${active ? "text-[#FF2A4D]" : "text-white"}`}>{track.title}</p>
          )}
          {track.artist ? (
            <Link
              to={`/app/artist/${encodeURIComponent(track.artist)}`}
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-zinc-400 hover:text-white hover:underline truncate block w-fit max-w-full"
            >
              {track.artist}
            </Link>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-1 relative">
        <button data-testid={`like-${track.id}`} onClick={like} title="Like"
          className={`p-1.5 rounded-md transition-colors ${liked ? "text-[#FF2A4D]" : "text-zinc-400 hover:text-white opacity-0 group-hover:opacity-100"}`}>
          <Heart className="w-4 h-4" fill={liked ? "#FF2A4D" : "none"} />
        </button>
        <span className="text-xs text-zinc-500 font-mono mr-2 hidden sm:block">{fmtTime(track.duration)}</span>
        {showAdd && (
          <div className="relative">
            <button data-testid={`add-track-${track.id}`} onClick={openMenu} className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" title="Add to playlist">
              <Plus className="w-4 h-4" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-9 z-50 w-56 bg-[#1c1c22] border border-white/10 rounded-lg shadow-2xl p-1.5 max-h-72 overflow-y-auto">
                  <button onClick={playNext} data-testid={`play-next-${track.id}`} className="w-full text-left text-sm px-2 py-1.5 rounded-md text-zinc-200 hover:bg-white/10 flex items-center gap-2">
                    <CornerDownRight className="w-3.5 h-3.5" /> {t("play_next")}
                  </button>
                  <button onClick={queueEnd} data-testid={`queue-end-${track.id}`} className="w-full text-left text-sm px-2 py-1.5 rounded-md text-zinc-200 hover:bg-white/10 flex items-center gap-2">
                    <ListEnd className="w-3.5 h-3.5" /> {t("add_to_queue")}
                  </button>
                  <button onClick={share} data-testid={`share-${track.id}`} className="w-full text-left text-sm px-2 py-1.5 rounded-md text-zinc-200 hover:bg-white/10 flex items-center gap-2">
                    <Share2 className="w-3.5 h-3.5" /> {t("share")}
                  </button>
                  <div className="h-px bg-white/10 my-1.5" />
                  <p className="text-xs text-zinc-500 px-2 py-1 flex items-center gap-1.5"><ListPlus className="w-3.5 h-3.5" /> {t("add_to_playlist")}</p>
                  {playlists.length === 0 ? (
                    <p className="text-xs text-zinc-500 px-2 py-2">{t("no_playlists")}</p>
                  ) : (
                    playlists.map((pl) => (
                      <button key={pl.id} onClick={() => addTo(pl.id)} data-testid={`add-to-${pl.id}`} className="w-full text-left text-sm px-2 py-1.5 rounded-md text-zinc-200 hover:bg-white/10 truncate">
                        {pl.name}
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}
        {onRemove && (
          <button data-testid={`remove-track-${track.id}`} onClick={() => onRemove(track)} className="p-1.5 rounded-md text-zinc-400 hover:text-red-400 hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" title="Quitar">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
